"use client";

import { useState, useEffect } from "react";
import { authFetch } from "@/lib/api";

interface NotionStatus {
  configured: boolean;
  default_database_id: string;
}

interface NotionDatabase {
  id: string;
  title: string;
  url: string;
}

export default function NotionSection() {
  const [status, setStatus] = useState<NotionStatus | null>(null);
  const [databases, setDatabases] = useState<NotionDatabase[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    authFetch("/api/notion/status")
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => {});
  }, []);

  const loadDatabases = async () => {
    try {
      const res = await authFetch("/api/notion/databases");
      const data = await res.json();
      if (data.success) {
        setDatabases(data.data.databases || []);
      } else {
        setError(data.error || "Failed to list databases");
      }
    } catch {
      setError("Failed to connect to Notion");
    }
  };

  const syncFromNotion = async (dbId?: string) => {
    setSyncing(true);
    setMessage("");
    setError("");
    try {
      const res = await authFetch("/api/notion/sync", {
        method: "POST",
        body: JSON.stringify({ notion_db_id: dbId }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage(data.data.message);
      } else {
        setError(data.error || "Sync failed");
      }
    } catch {
      setError("Sync request failed");
    } finally {
      setSyncing(false);
    }
  };

  const pushToNotion = async (dbId?: string) => {
    setPushing(true);
    setMessage("");
    setError("");
    try {
      const res = await authFetch("/api/notion/push", {
        method: "POST",
        body: JSON.stringify({ notion_db_id: dbId }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage(data.data.message);
      } else {
        setError(data.error || "Push failed");
      }
    } catch {
      setError("Push request failed");
    } finally {
      setPushing(false);
    }
  };

  if (!status?.configured) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-4" data-testid="settings-notion-section">
        <h2 className="text-sm font-semibold text-text-light mb-1">Notion Integration</h2>
        <p className="text-[10px] text-muted mb-3">
          Sync tasks between DecisionEase and Notion
        </p>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-muted/15 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="stroke-muted" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-medium text-text-light">Not configured</p>
            <p className="text-[10px] text-muted">Set NOTION_API_KEY in server env</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-4" data-testid="settings-notion-section">
      <h2 className="text-sm font-semibold text-text-light mb-1">Notion Integration</h2>
      <p className="text-[10px] text-muted mb-3">
        Sync tasks between DecisionEase and Notion
      </p>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="stroke-accent" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-medium text-text-light">Connected</p>
            <p className="text-[10px] text-muted">Notion API configured</p>
          </div>
        </div>
      </div>

      {/* Sync actions */}
      <div className="flex gap-2 mb-2">
        <button
          data-testid="settings-notion-pull"
          onClick={() => syncFromNotion()}
          disabled={syncing}
          className="flex-1 py-2 px-3 rounded-xl bg-primary text-white text-xs font-medium
                     hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {syncing ? "Syncing..." : "Pull from Notion"}
        </button>
        <button
          data-testid="settings-notion-push"
          onClick={() => pushToNotion()}
          disabled={pushing}
          className="flex-1 py-2 px-3 rounded-xl border border-border text-text-light text-xs font-medium
                     hover:bg-bg transition-colors disabled:opacity-50"
        >
          {pushing ? "Pushing..." : "Push to Notion"}
        </button>
      </div>

      {/* Browse databases */}
      {databases.length === 0 && (
        <button
          data-testid="settings-notion-browse"
          onClick={loadDatabases}
          className="w-full py-1.5 text-[10px] text-blue hover:underline"
        >
          Browse available databases
        </button>
      )}

      {databases.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-[10px] text-muted font-medium">Available databases:</p>
          {databases.map((db) => (
            <div key={db.id} className="flex items-center justify-between py-1 px-2 bg-bg rounded-lg">
              <span className="text-[10px] text-text-light truncate flex-1">{db.title}</span>
              <button
                onClick={() => syncFromNotion(db.id)}
                className="text-[10px] text-blue hover:underline ml-2"
              >
                Sync
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Feedback messages */}
      {message && (
        <p className="mt-2 text-[10px] text-accent">{message}</p>
      )}
      {error && (
        <p className="mt-2 text-[10px] text-amber">{error}</p>
      )}
    </div>
  );
}
